export type ClauseSection = 'contract' | 'warranty';

export type ContractClause = {
  id: string;
  title: string;
  html: string;
  requiresInitial: boolean;
  /** Section IV vs V — Option B roster/rail spans both. */
  section?: ClauseSection;
};

export type ClauseConfig = {
  id: string;
  title: string;
  requiresInitial: boolean;
};

export function clauseSlug(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'clause'
  );
}

export function clauseId(title: string, index: number): string {
  return `${index}-${clauseSlug(title)}`;
}

export function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function looksLikeLegalHeading(text: string): boolean {
  const title = text.trim();
  if (title.length < 3 || title.length > 90) return false;
  if (/^\d+(\.\d+)*[.)]\s+\S/.test(title)) return true;
  if (/^article\s+\d+/i.test(title)) return true;
  return title === title.toUpperCase() && /[A-Z]/.test(title);
}

export function promotePlainHeadings(html: string): string {
  let out = html.replace(/^(#{1,6})\s+(.+)$/gm, (_, hashes: string, title: string) => {
    const level = Math.min(hashes.length, 6);
    return `<h${level}>${title.trim()}</h${level}>`;
  });
  out = out.replace(
    /<p>\s*<(?:strong|b)>([\s\S]*?)<\/(?:strong|b)>\s*<\/p>/gi,
    (full, title: string) => {
      const text = stripTags(title);
      return looksLikeLegalHeading(text) ? `<h3>${text}</h3>` : full;
    },
  );
  return out;
}

export function splitHtmlIntoClauses(
  body: string,
  fallbackTitle = 'Terms & Conditions',
): Array<Omit<ContractClause, 'requiresInitial'>> {
  const html = promotePlainHeadings((body ?? '').trim());
  if (!html) return [];
  const headingRe = /<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1>/gi;
  const matches = [...html.matchAll(headingRe)];
  if (matches.length === 0) {
    return [{ id: clauseId(fallbackTitle, 0), title: fallbackTitle, html }];
  }
  const clauses: Array<Omit<ContractClause, 'requiresInitial'>> = [];
  const firstIndex = matches[0].index ?? 0;
  const leading = html.slice(0, firstIndex).trim();
  if (leading) {
    clauses.push({
      id: clauseId('General', clauses.length),
      title: 'General',
      html: leading,
    });
  }
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const title = stripTags(match[2] ?? '') || fallbackTitle;
    const start = (match.index ?? 0) + match[0].length;
    const end =
      i + 1 < matches.length ? (matches[i + 1].index ?? html.length) : html.length;
    clauses.push({
      id: clauseId(title, clauses.length),
      title,
      html: html.slice(start, end).trim(),
    });
  }
  return clauses;
}

export function applyClauseConfig(
  clauses: Array<Omit<ContractClause, 'requiresInitial'>>,
  config?: ClauseConfig[] | null,
): ContractClause[] {
  const rows = Array.isArray(config) ? config : [];
  const byId = new Map(rows.map((c) => [c.id, c]));
  const byTitle = new Map(rows.map((c) => [clauseSlug(c.title), c]));
  return clauses.map((clause) => {
    const hit = byId.get(clause.id) || byTitle.get(clauseSlug(clause.title));
    return { ...clause, requiresInitial: hit?.requiresInitial === true };
  });
}

export function buildClauseManifest(
  html: string,
  config?: ClauseConfig[] | null,
  section: ClauseSection = 'contract',
): ContractClause[] {
  return applyClauseConfig(
    splitHtmlIntoClauses(
      html,
      section === 'warranty' ? 'Limited Warranty' : 'Terms & Conditions',
    ),
    config,
  ).map((clause) => ({
    ...clause,
    id: section === 'warranty' ? `warranty:${clause.id}` : clause.id,
    section,
  }));
}

/** Option B · Client initials roster spanning Contract Language + Warranty. */
export function buildClientInitialsManifest(
  contractHtml: string,
  warrantyHtml: string,
  contractConfig?: ClauseConfig[] | null,
  warrantyConfig?: ClauseConfig[] | null,
): ContractClause[] {
  return [
    ...buildClauseManifest(contractHtml, contractConfig, 'contract'),
    ...buildClauseManifest(warrantyHtml, warrantyConfig, 'warranty'),
  ];
}

export function contractSectionClauses(clauses: ContractClause[]): ContractClause[] {
  return clauses.filter(
    (clause) => !clause.section || clause.section === 'contract',
  );
}

export function sectionLabel(section?: ClauseSection): string {
  return section === 'warranty'
    ? 'Warranty · Section V'
    : 'Contract Language · Section IV';
}

/** Config rows stored on the pack (ids without warranty: prefix). */
export function configFromManifestSection(
  clauses: ContractClause[],
  section: ClauseSection,
): ClauseConfig[] {
  return clauses
    .filter((clause) => (clause.section ?? 'contract') === section)
    .map((clause) => ({
      id:
        section === 'warranty' && clause.id.startsWith('warranty:')
          ? clause.id.slice('warranty:'.length)
          : clause.id,
      title: clause.title,
      requiresInitial: clause.requiresInitial,
    }));
}

export function clausesFromSource(input: {
  html?: string | null;
  warrantyHtml?: string | null;
  frozen?: ContractClause[] | null;
  config?: ClauseConfig[] | null;
  warrantyConfig?: ClauseConfig[] | null;
}): ContractClause[] {
  if (Array.isArray(input.frozen) && input.frozen.length > 0) {
    return input.frozen.map((clause) => ({
      id: String(clause.id),
      title: String(clause.title ?? 'Clause'),
      html: String(clause.html ?? ''),
      requiresInitial: clause.requiresInitial === true,
      section:
        clause.section === 'warranty'
          ? 'warranty'
          : String(clause.id).startsWith('warranty:')
            ? 'warranty'
            : 'contract',
    }));
  }
  if (input.warrantyHtml != null || input.warrantyConfig != null) {
    return buildClientInitialsManifest(
      input.html ?? '',
      input.warrantyHtml ?? '',
      input.config,
      input.warrantyConfig,
    );
  }
  return applyClauseConfig(splitHtmlIntoClauses(input.html ?? ''), input.config);
}

export function requiredClauses(clauses: ContractClause[]): ContractClause[] {
  return clauses.filter((clause) => clause.requiresInitial);
}

/** Leading section reference from a clause title (e.g. 12.5.2). */
export function clauseRef(title: string): string {
  const match = String(title ?? '')
    .trim()
    .match(/^(\d+(?:\.\d+)*|[IVXLC]+\.?\d*)\b/i);
  return match?.[1] ?? '';
}

export function clauseTitleBody(title: string): string {
  const ref = clauseRef(title);
  if (!ref) return String(title ?? '').trim();
  return String(title ?? '')
    .trim()
    .slice(ref.length)
    .replace(/^[\s.—:-]+/, '')
    .trim() || String(title ?? '').trim();
}

export function capturedInitialsFromInput(
  initials?:
    | Array<{ clauseId?: string; text?: string }>
    | Record<string, { text?: string } | string>
    | null,
): Record<string, string> {
  const map: Record<string, string> = {};
  if (Array.isArray(initials)) {
    for (const row of initials) {
      const id = String(row?.clauseId ?? '').trim();
      const text = String(row?.text ?? '').trim();
      if (id && text) map[id] = text;
    }
    return map;
  }
  if (initials && typeof initials === 'object') {
    for (const [id, row] of Object.entries(initials)) {
      const text =
        typeof row === 'string'
          ? row.trim()
          : String((row as { text?: string })?.text ?? '').trim();
      if (id && text) map[id] = text;
    }
  }
  return map;
}
